import { memo, useCallback, useMemo, useRef, useState } from "react";
import { Label } from "src/components/ui/label";
import { Switch } from "src/components/ui/switch";
import { useCachedState } from "src/hooks/useCache";
import { cn } from "src/utilities/shadcnUtils";

interface TogglePresentationalProps {
  id: string;
  label: string;
  direction: "left" | "right";
  on: boolean;
  onToggle: () => void;
  className?: string;
}

const TogglePresentational = memo(function TogglePresentational({
  id,
  label,
  direction,
  on,
  onToggle,
  className,
}: TogglePresentationalProps) {
  return (
    <div className={cn("flex items-center space-x-2", className)}>
      {direction === "left" ? (
        <Label className="cursor-pointer" htmlFor={id}>
          {label}
        </Label>
      ) : null}
      <Switch checked={on} id={id} onCheckedChange={onToggle} />
      {direction === "right" ? (
        <Label className="cursor-pointer" htmlFor={id}>
          {label}
        </Label>
      ) : null}
    </div>
  );
});

export default function useToggler({
  id,
  label,
  defaultValue = false,
  direction = "right",
  cacheKey,
}: {
  id: string;
  label: string;
  defaultValue: boolean;
  direction?: "left" | "right";
  cacheKey?: string;
}) {
  const [on, setOn] = (cacheKey ? useCachedState : useState)<boolean>(defaultValue, cacheKey!);
  const handleToggle = useCallback(() => {
    setOn((prev) => !prev);
  }, [setOn]);

  // Keep latest values in a ref so Toggle's identity can stay stable across
  // renders while still reading current state when it re-renders with its parent.
  const latestRef = useRef({ id, label, direction, on, handleToggle });
  latestRef.current = { id, label, direction, on, handleToggle };

  const Toggle = useMemo(
    () =>
      function Toggle({ className }: { className?: string }) {
        const { id, label, direction, on, handleToggle } = latestRef.current;
        return (
          <TogglePresentational
            id={id}
            label={label}
            direction={direction}
            on={on}
            onToggle={handleToggle}
            className={className}
          />
        );
      },
    []
  );

  return useMemo(
    () => ({
      on,
      setOn,
      Toggle,
    }),
    [on, setOn, Toggle]
  );
}
